import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Headphones, ExternalLink, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

/**
 * Remote Support button backed by Zoho Assist.
 * - context="client": logged-in client portal — creates a session directly.
 * - context="public": marketing/contact — collects name+email first.
 */
const RemoteSupportButton = ({ language = 'en', variant = 'default', className = '', context = 'public', payload = null }) => {
  const [available, setAvailable] = useState(false);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: '', email: '' });
  const [sessionUrl, setSessionUrl] = useState(null);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const { data } = await api.get('/remote-support/available');
        setAvailable(!!data.available);
      } catch (e) {
        setAvailable(false);
      }
    };
    fetchStatus();
  }, []);

  const buttonText = language === 'fr' ? 'Support à distance' : 'Remote Support';

  const startClientSession = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post('/remote-support/request', payload || {});
      setSessionUrl(data.customer_url);
      window.open(data.customer_url, '_blank', 'noopener');
      toast.success(language === 'fr' ? 'Session créée — vous êtes dans la file d\'attente.' : 'Session created — you are now in the support queue.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start a remote session.');
    } finally {
      setSubmitting(false);
    }
  };

  const submitPublicSession = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error('Please enter your name and email.');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post('/remote-support/public-request', { name: form.name, email: form.email });
      setSessionUrl(data.customer_url);
      window.open(data.customer_url, '_blank', 'noopener');
      toast.success(language === 'fr' ? 'Session créée — vous êtes dans la file d\'attente.' : 'Session created — you are now in the support queue.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start a remote session.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClick = () => {
    setSessionUrl(null);
    if (context === 'client') {
      // Open a confirmation dialog so the user knows a session is being created.
      setOpen(true);
    } else {
      setForm({ name: '', email: '' });
      setOpen(true);
    }
  };

  if (!available) return null;

  const renderTrigger = () => {
    if (variant === 'card') {
      return (
        <div className="p-5 bg-green-50 rounded-lg border border-green-200" data-testid="remote-support-card">
          <h4 className="font-semibold text-slate-900 mb-1">{language === 'fr' ? 'Besoin d\'aide immédiate ?' : 'Need help right now?'}</h4>
          <p className="text-slate-600 text-sm mb-3">
            {language === 'fr'
              ? 'Démarrez une session de support à distance sécurisée et un technicien vous rejoindra.'
              : 'Start a secure remote support session and a technician will join you.'}
          </p>
          <Button onClick={handleClick} className="bg-green-600 hover:bg-green-700 text-white flex items-center gap-2" data-testid="remote-support-btn">
            <Headphones className="h-4 w-4" /> {buttonText}
          </Button>
        </div>
      );
    }
    if (variant === 'hero') {
      return (
        <Button onClick={handleClick} className={`bg-green-600 hover:bg-green-700 text-white flex items-center gap-2 ${className}`} data-testid="remote-support-btn">
          <Headphones className="h-4 w-4" /> {buttonText} <ExternalLink className="h-3 w-3" />
        </Button>
      );
    }
    if (variant === 'sidebar') {
      return (
        <button onClick={handleClick} className={`w-full flex items-center gap-3 px-3 py-2 text-sm rounded-lg text-green-700 bg-green-50 hover:bg-green-100 transition-colors ${className}`} data-testid="remote-support-sidebar-btn">
          <Headphones className="h-4 w-4" /> <span>{buttonText}</span> <ExternalLink className="h-3 w-3 ml-auto" />
        </button>
      );
    }
    if (variant === 'floating') {
      return (
        <button onClick={handleClick} className={`fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all ${className}`} data-testid="remote-support-floating-btn">
          <Headphones className="h-5 w-5" /> <span className="hidden sm:inline">{buttonText}</span>
        </button>
      );
    }
    return (
      <Button onClick={handleClick} variant="outline" className={`border-green-600 text-green-600 hover:bg-green-50 flex items-center gap-2 ${className}`} data-testid="remote-support-btn">
        <Headphones className="h-4 w-4" /> {buttonText}
      </Button>
    );
  };

  return (
    <>
      {renderTrigger()}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md bg-white" data-testid="remote-support-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Headphones className="h-5 w-5 text-green-600" /> {buttonText}
            </DialogTitle>
            <DialogDescription>
              {language === 'fr'
                ? 'Démarrez une session Zoho Assist. Un technicien vous rejoindra sous peu.'
                : 'Start a Zoho Assist remote session. A technician will join you shortly.'}
            </DialogDescription>
          </DialogHeader>

          {sessionUrl ? (
            <div className="space-y-3 py-2" data-testid="remote-support-success">
              <div className="flex items-center gap-2 text-green-700">
                <CheckCircle className="h-5 w-5" />
                <span className="text-sm font-medium">
                  {language === 'fr' ? 'Vous êtes dans la file d\'attente.' : 'You are in the support queue.'}
                </span>
              </div>
              <p className="text-sm text-slate-600">
                {language === 'fr'
                  ? 'Si la fenêtre ne s\'est pas ouverte, utilisez le lien ci-dessous :'
                  : 'If the window did not open, use the link below:'}
              </p>
              <a href={sessionUrl} target="_blank" rel="noopener noreferrer" className="block text-sm text-green-700 underline break-all" data-testid="remote-support-join-link">
                {sessionUrl}
              </a>
            </div>
          ) : context === 'public' ? (
            <div className="space-y-3 py-2">
              <div className="space-y-1">
                <Label htmlFor="rs-name">{language === 'fr' ? 'Nom' : 'Name'}</Label>
                <Input id="rs-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={language === 'fr' ? 'Votre nom' : 'Your name'} data-testid="remote-support-name-input" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="rs-email">Email</Label>
                <Input id="rs-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" data-testid="remote-support-email-input" />
              </div>
            </div>
          ) : (
            <div className="py-2 text-sm text-slate-600">
              {payload?.ticket_number && (
                <p className="mb-2 font-medium text-slate-800" data-testid="remote-support-ticket-ref">
                  {language === 'fr' ? 'Billet' : 'Ticket'} #{payload.ticket_number}
                </p>
              )}
              {language === 'fr'
                ? 'Cliquez sur Démarrer pour générer votre session de support à distance.'
                : 'Click Start to generate your remote support session.'}
            </div>
          )}

          {!sessionUrl && (
            <DialogFooter>
              <Button
                onClick={context === 'public' ? submitPublicSession : startClientSession}
                disabled={submitting}
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="remote-support-start-btn"
              >
                {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Headphones className="h-4 w-4 mr-2" />}
                {language === 'fr' ? 'Démarrer la session' : 'Start Session'}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RemoteSupportButton;
